/**
 * Shared helpers for the careers/job data.
 *
 * Used both at build time (gatsby-node.js, CommonJS) and in the browser
 * bundle (Jobs list, job template), so this file stays CommonJS.
 */

const jobsData = require("../components/Careers/_jobs.json");

const slugify = (value = "") =>
  value
    .toString()
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

const formatLocationBadge = (location = "") => {
  const value = location.trim();
  if (!value.includes(",")) {
    return value;
  }

  const parts = value
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean);
  if (parts.length < 2) {
    return value;
  }

  return parts[parts.length - 1] || location;
};

/**
 * Flattens _jobs.json into a list of jobs, each with a stable id, a unique
 * URL slug, and the owning company. Slug collisions get a numeric suffix so
 * every opening keeps its own indexable URL.
 */
const getAllJobs = () => {
  const usedSlugs = new Map();

  return jobsData.flatMap((company) =>
    company.jobOpenings.map((job, index) => {
      const baseSlug =
        slugify(
          [job.positionTitle, job.locationNickname || job.location]
            .filter(Boolean)
            .join(" "),
        ) || slugify(`${company.companyCode}-${index}`);

      const seen = usedSlugs.get(baseSlug) || 0;
      usedSlugs.set(baseSlug, seen + 1);
      const slug = seen === 0 ? baseSlug : `${baseSlug}-${seen + 1}`;

      return {
        ...job,
        companyCode: company.companyCode,
        companyName: company.companyName,
        id: `${company.companyCode}-${index}`,
        slug,
        path: `/careers/${slug}/`,
      };
    }),
  );
};

/** Plain-text summary used for meta descriptions (kept under ~160 chars). */
const getJobMetaDescription = (job) => {
  const intro = `${job.positionTitle} opening at ${job.companyName} in ${formatLocationBadge(
    job.location,
  )}.`;
  const first = (job.jobDescription || [])[0] || "";
  const description = `${intro} ${first}`.trim();

  return description.length > 157
    ? `${description.slice(0, 157).trimEnd()}...`
    : description;
};

const EMPLOYMENT_TYPE_MAP = {
  permanent: "FULL_TIME",
  "fixed term": "TEMPORARY",
  "full time": "FULL_TIME",
  "part time": "PART_TIME",
  contract: "CONTRACTOR",
  internship: "INTERN",
};

const toSchemaEmploymentType = (employmentType = "") =>
  EMPLOYMENT_TYPE_MAP[employmentType.trim().toLowerCase()] || "OTHER";

const toPostalAddress = (location = "") => {
  const parts = location
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean);

  const last = parts[parts.length - 1] || location;
  let addressLocality = last;
  let addressRegion;

  if (parts.length >= 3) {
    // "San Miguel, Bauan, Batangas" -> locality Bauan, region Batangas
    addressLocality = parts[parts.length - 2];
    addressRegion = last;
  } else if (parts.length === 2 && !/city/i.test(last)) {
    // "Cagayan De Oro, Misamis Oriental" -> locality city, region province
    addressLocality = parts[0];
    addressRegion = last;
  }

  const address = {
    "@type": "PostalAddress",
    streetAddress: location,
    addressLocality,
    addressCountry: "PH",
  };

  if (addressRegion && addressRegion !== addressLocality) {
    address.addressRegion = addressRegion;
  }

  return address;
};

/** BreadcrumbList so search results show Careers > <role>. */
const getJobBreadcrumbSchema = (job, siteUrl) => {
  const base = siteUrl.replace(/\/$/, "");

  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Careers",
        item: `${base}/careers/`,
      },
      {
        "@type": "ListItem",
        position: 2,
        name: job.positionTitle,
        item: `${base}${job.path}`,
      },
    ],
  };
};

/**
 * Google Jobs (JobPosting) structured data.
 * `datePosted` comes from the job entry when present, otherwise the build date.
 */
const getJobPostingSchema = (job, siteUrl, buildDate) => {
  const url = `${siteUrl.replace(/\/$/, "")}${job.path}`;
  const descriptionHtml = [
    "<p>",
    `Apply now as a ${job.positionTitle} at ${job.companyName.replace(/\.$/, "")}.`,
    "</p>",
    "<h3>Job Description</h3><ul>",
    ...(job.jobDescription || []).map((item) => `<li>${item}</li>`),
    "</ul>",
    "<h3>Qualifications</h3><ul>",
    ...(job.qualifications || []).map((item) => `<li>${item}</li>`),
    "</ul>",
  ].join("");

  const schema = {
    "@context": "https://schema.org",
    "@type": "JobPosting",
    title: job.positionTitle,
    description: descriptionHtml,
    identifier: {
      "@type": "PropertyValue",
      name: job.companyName,
      value: job.id,
    },
    datePosted: (job.datePosted || buildDate || "").slice(0, 10),
    employmentType: toSchemaEmploymentType(job.employmentType),
    hiringOrganization: {
      "@type": "Organization",
      name: job.companyName,
      sameAs: siteUrl,
    },
    jobLocation: {
      "@type": "Place",
      address: toPostalAddress(job.location),
    },
    directApply: true,
    url,
  };

  if (job.validThrough) {
    schema.validThrough = job.validThrough;
  }

  return schema;
};

module.exports = {
  slugify,
  formatLocationBadge,
  getAllJobs,
  getJobMetaDescription,
  getJobPostingSchema,
  getJobBreadcrumbSchema,
  toSchemaEmploymentType,
};
